import React from 'react';
import {
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Loader2,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import type {
  AgencyResponse,
  RoleResponse,
  UserManagerUserResponse,
} from '../../../services/api/types.gen';
import type { AgencyState } from '../types';

// ---------------------------------------------------------------------------
// UserRow
// ---------------------------------------------------------------------------

interface UserRowProps {
  user: UserManagerUserResponse;
  index: number;
  onEdit: (user: UserManagerUserResponse) => void;
  onRemove: (user: UserManagerUserResponse) => void;
}

const UserRow: React.FC<UserRowProps> = ({ user, index, onEdit, onRemove }) => {
  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') || '—';
  return (
    <div
      className={`flex items-center justify-between px-4 py-2.5 border-b border-border/50 last:border-0 hover:bg-muted/60 transition-colors group ${index % 2 === 0 ? 'bg-muted/40' : 'bg-background'}`}
    >
      <button
        className='flex flex-col items-start text-left gap-0.5 flex-1 min-w-0'
        onClick={() => onEdit(user)}
      >
        <span className='text-sm font-medium truncate'>{fullName}</span>
        <span className='text-xs text-muted-foreground truncate'>
          {user.email}
        </span>
      </button>
      <div className='flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity'>
        <Button
          size='sm'
          variant='ghost'
          onClick={() => onEdit(user)}
          className='h-7 px-2 text-xs'
        >
          Edit roles
        </Button>
        <Button
          size='sm'
          variant='ghost'
          onClick={() => onRemove(user)}
          className='h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10'
        >
          <UserMinus className='h-3.5 w-3.5' />
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AgencyCardRolePill
// ---------------------------------------------------------------------------

interface AgencyCardRolePillProps {
  role: RoleResponse;
}

const AgencyCardRolePill: React.FC<AgencyCardRolePillProps> = ({ role }) => {
  const description = role.description?.trim();
  const badge = (
    <Badge variant='secondary' className='text-xs font-normal'>
      {role.name}
    </Badge>
  );

  if (!description) {
    return badge;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='inline-flex max-w-full cursor-default'>{badge}</span>
      </TooltipTrigger>
      <TooltipContent side='top' className='max-w-sm text-left'>
        {description}
      </TooltipContent>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// AgencyRow
// ---------------------------------------------------------------------------

export interface AgencyRowProps {
  agency: AgencyResponse;
  state: AgencyState;
  onToggleExpand: (agencyId: string) => void;
  onEditUser: (agency: AgencyResponse, user: UserManagerUserResponse) => void;
  onRemoveUser: (agency: AgencyResponse, user: UserManagerUserResponse) => void;
  onAddUser: (agency: AgencyResponse) => void;
}

const AgencyRow: React.FC<AgencyRowProps> = ({
  agency,
  state,
  onToggleExpand,
  onEditUser,
  onRemoveUser,
  onAddUser,
}) => {
  const { isExpanded, users, isLoadingUsers, assignedRoles } = state;

  return (
    <div>
      <div className='flex items-center gap-2 px-4 py-3 hover:bg-muted/30 transition-colors group'>
        <button
          className='flex items-center gap-3 flex-1 min-w-0 text-left'
          onClick={() => onToggleExpand(agency.id)}
        >
          <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-muted flex-shrink-0'>
            <Building2 className='h-4 w-4 text-muted-foreground' />
          </div>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center gap-2 flex-wrap'>
              <span className='text-sm font-medium'>{agency.written_name}</span>
              <Badge
                variant='outline'
                className='text-xs font-mono px-1.5 py-0'
              >
                {agency.abbreviation}
              </Badge>
              <span className='text-xs text-muted-foreground hidden sm:block'>
                {agency.system_name}
              </span>
            </div>
            <div className='mt-1 flex flex-wrap gap-1 items-center min-h-[18px]'>
              {assignedRoles.length > 0 ? (
                <TooltipProvider delayDuration={300}>
                  {assignedRoles.map(role => (
                    <AgencyCardRolePill key={role.id} role={role} />
                  ))}
                </TooltipProvider>
              ) : (
                <span className='text-xs text-muted-foreground'>
                  No Roger RBAC roles in use
                </span>
              )}
            </div>
          </div>
          <div className='flex items-center gap-3 flex-shrink-0'>
            {users !== null && (
              <span className='text-xs text-muted-foreground flex items-center gap-1'>
                <Users className='h-3 w-3' />
                {users.length}
              </span>
            )}
            {isExpanded ? (
              <ChevronDown className='h-4 w-4 text-muted-foreground' />
            ) : (
              <ChevronRight className='h-4 w-4 text-muted-foreground' />
            )}
          </div>
        </button>
        <Button
          size='sm'
          variant='ghost'
          onClick={() => onAddUser(agency)}
          className='flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity h-7 px-2 text-xs gap-1'
        >
          <UserPlus className='h-3 w-3' />
          Add user
        </Button>
      </div>

      {isExpanded && (
        <div className='border-t border-border/50 bg-muted/20'>
          {isLoadingUsers ? (
            <div className='flex items-center justify-center py-6 gap-2 text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span className='text-sm'>Loading users…</span>
            </div>
          ) : users && users.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-6 gap-2 text-muted-foreground'>
              <Users className='h-4 w-4' />
              <span className='text-sm'>No users in this agency</span>
              <Button
                size='sm'
                variant='ghost'
                onClick={() => onAddUser(agency)}
                className='h-7 px-2 text-xs gap-1'
              >
                <UserPlus className='h-3 w-3' />
                Invite someone
              </Button>
            </div>
          ) : (
            <div>
              {users?.map((user, index) => (
                <UserRow
                  key={user.id}
                  user={user}
                  index={index}
                  onEdit={u => onEditUser(agency, u)}
                  onRemove={u => onRemoveUser(agency, u)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgencyRow;
